import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from './api';

/** Centralised query keys so realtime handlers and mutations update the same cache entries. */
export const keys = {
  me: ['me'] as const,
  sessions: ['sessions'] as const,
  teams: ['teams'] as const,
  members: (teamId: string) => ['team', teamId, 'members'] as const,
  roles: (teamId: string) => ['team', teamId, 'roles'] as const,
  calls: (teamId: string) => ['team', teamId, 'calls'] as const,
  callsConfig: ['calls', 'config'] as const,
  channels: (teamId: string) => ['team', teamId, 'channels'] as const,
  tasks: (teamId: string) => ['team', teamId, 'tasks'] as const,
  sprints: (teamId: string) => ['team', teamId, 'sprints'] as const,
  columns: (teamId: string) => ['team', teamId, 'columns'] as const,
  teamInvites: (teamId: string) => ['team', teamId, 'invites'] as const,
  messages: (channelId: string) => ['channel', channelId, 'messages'] as const,
  thread: (parentId: string) => ['thread', parentId] as const,
  message: (messageId: string) => ['message', messageId] as const,
  meetings: (teamId: string) => ['team', teamId, 'meetings'] as const,
  meeting: (meetingId: string) => ['meeting', meetingId] as const,
  pins: (channelId: string) => ['channel', channelId, 'pins'] as const,
  saved: (teamId: string) => ['team', teamId, 'saved'] as const,
  savedIds: (teamId: string) => ['team', teamId, 'saved', 'ids'] as const,
  myInvites: ['invites', 'mine'] as const,
  notifications: ['notifications'] as const,
  search: (teamId: string, q: string) => ['team', teamId, 'search', q] as const,
  realtimeConfig: ['realtime', 'config'] as const,
  files: (channelId: string) => ['channel', channelId, 'files'] as const,
  webhooks: (channelId: string) => ['channel', channelId, 'webhooks'] as const,
  attachmentsConfig: ['attachments', 'config'] as const,
  features: ['platform', 'features'] as const,
  reminders: (teamId: string) => ['team', teamId, 'reminders'] as const,
  scheduled: (channelId: string) => ['channel', channelId, 'scheduled'] as const,
  directory: (q: string) => ['directory', q] as const,
};

/** Platform switches (set by the Nexo team in /admin) and the official server. */
export const useFeatures = () =>
  useQuery({ queryKey: keys.features, queryFn: api.platform.features, staleTime: 60_000 });

export const useTeams = () => useQuery({ queryKey: keys.teams, queryFn: api.teams.list });

export const useMembers = (teamId: string) =>
  useQuery({ queryKey: keys.members(teamId), queryFn: () => api.teams.members(teamId) });

export const useRoles = (teamId: string) =>
  useQuery({ queryKey: keys.roles(teamId), queryFn: () => api.roles.list(teamId) });

export const useCallsConfig = () =>
  useQuery({ queryKey: keys.callsConfig, queryFn: api.calls.config, staleTime: Infinity });

/** Calls in progress in the team (kept live by `call:updated` events). */
export const useActiveCalls = (teamId: string) =>
  useQuery({
    queryKey: keys.calls(teamId),
    queryFn: () => api.calls.active(teamId),
    enabled: useCallsConfig().data?.enabled === true,
    // Safety net if a realtime event is missed.
    refetchInterval: 30_000,
  });

export const useChannels = (teamId: string) =>
  useQuery({ queryKey: keys.channels(teamId), queryFn: () => api.channels.list(teamId) });

/** All tasks of a team (small teams: filtered client-side, kept fresh by realtime events). */
export const useTasks = (teamId: string) =>
  useQuery({ queryKey: keys.tasks(teamId), queryFn: () => api.tasks.list(teamId) });

/** Columns of the team's board, in order (custom states, each in a status category). */
export const useColumns = (teamId: string) =>
  useQuery({ queryKey: keys.columns(teamId), queryFn: () => api.columns.list(teamId) });

/** Open sprints (active first, then planned) and the latest completed ones. */
export const useSprints = (teamId: string) =>
  useQuery({ queryKey: keys.sprints(teamId), queryFn: () => api.sprints.list(teamId) });

export const useMyInvites = () => useQuery({ queryKey: keys.myInvites, queryFn: api.invites.mine });

export const useNotifications = () =>
  useQuery({ queryKey: keys.notifications, queryFn: api.notifications.list });

export const useMessages = (channelId: string) =>
  useInfiniteQuery({
    queryKey: keys.messages(channelId),
    queryFn: ({ pageParam }) =>
      api.messages.list(channelId, pageParam ? { before: pageParam } : {}),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
  });

export const useSearch = (teamId: string, q: string) =>
  useQuery({
    queryKey: keys.search(teamId, q),
    queryFn: () => api.search(teamId, q),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
  });

export const useChannelFiles = (channelId: string) =>
  useQuery({ queryKey: keys.files(channelId), queryFn: () => api.attachments.list(channelId) });

export const useWebhooks = (channelId: string) =>
  useQuery({ queryKey: keys.webhooks(channelId), queryFn: () => api.webhooks.list(channelId) });

export const useAttachmentsConfig = () =>
  useQuery({
    queryKey: keys.attachmentsConfig,
    queryFn: api.attachments.config,
    staleTime: Infinity,
  });

export const useThread = (parentId: string) =>
  useQuery({ queryKey: keys.thread(parentId), queryFn: () => api.messages.replies(parentId) });

export const usePins = (channelId: string) =>
  useQuery({ queryKey: keys.pins(channelId), queryFn: () => api.messages.pins(channelId) });

export const useSaved = (teamId: string) =>
  useQuery({ queryKey: keys.saved(teamId), queryFn: () => api.messages.saved(teamId) });

/** Ids of my saved messages in the team, for the bookmark state on each message. */
export const useSavedIds = (teamId: string) =>
  useQuery({
    queryKey: keys.savedIds(teamId),
    queryFn: () => api.messages.savedIds(teamId),
    select: (ids) => new Set(ids),
  });

export const useMeetings = (teamId: string) =>
  useQuery({ queryKey: keys.meetings(teamId), queryFn: () => api.meetings.list(teamId) });

export const useMeeting = (meetingId: string) =>
  useQuery({ queryKey: keys.meeting(meetingId), queryFn: () => api.meetings.get(meetingId) });
